import * as React from "react";
import styled from "styled-components";
import { theme } from "styles/theme";

type Props = {
  name?: string;
  value?: string;
  onValueChange: (digits: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
};

const StyledInput = styled.input`
  outline: none;
  font-weight: 500;
  border: 1px solid ${theme.border.light};
  border-radius: 5px;
  background: white;
  margin: 0;
  height: ${theme.height.regular};
  padding: 0 0.5em;
  transition: all 0.09s linear;
  font-size: ${theme.fontSize.regular};
  font-variant-numeric: tabular-nums;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  letter-spacing: 0.02em;
  &:disabled {
    background: ${theme.bg.blue};
    cursor: not-allowed;
  }
  &:hover {
    border: 1px solid #5d6d7e;
  }
  &:focus {
    background: transparent;
    border: 1px solid #3498db;
  }
`;

/** e.g. 021430 → 02d : 14h : 30m */
export const formatLeadTimeDigitsDisplay = (digits: string): string => {
  const filled = digits.padStart(6, "0");
  const days = filled.slice(0, 2);
  const hours = filled.slice(2, 4);
  const minutes = filled.slice(4, 6);
  return `${days}d : ${hours}h : ${minutes}m`;
};

const isValid = (digits: string): boolean => {
  if (digits.length >= 2) {
    const minutes = parseInt(digits.slice(-2), 10);
    if (!Number.isFinite(minutes) || minutes > 59) return false;
  }
  if (digits.length >= 4) {
    const hours = parseInt(digits.slice(-4, -2), 10);
    if (!Number.isFinite(hours) || hours > 99) return false;
  }
  if (digits.length >= 6) {
    const days = parseInt(digits.slice(-6, -4), 10);
    if (!Number.isFinite(days) || days > 99) return false;
  }
  return true;
};

export default function MaskedLeadTimeInput({
  value = "",
  onValueChange,
  placeholder = "00d : 00h : 00m",
  ...rest
}: Props) {
  const digits = value.replace(/[^0-9]/g, "").slice(-6);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" || e.key === "Delete") {
      e.preventDefault();
      if (digits.length === 0) return;
      onValueChange(digits.slice(0, -1));
      return;
    }
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      const next = (digits + e.key).slice(-6);
      if (!isValid(next)) return;
      onValueChange(next);
      return;
    }
    const allow = [
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
      "Enter",
      "Escape",
    ];
    if (!allow.includes(e.key) && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/[^0-9]/g, "");
    if (!pasted) return;
    const next = (digits + pasted).slice(-6);
    if (!isValid(next)) return;
    onValueChange(next);
  };

  return (
    <StyledInput
      type="text"
      inputMode="numeric"
      value={formatLeadTimeDigitsDisplay(digits)}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onChange={() => {}}
      placeholder={placeholder}
      {...rest}
    />
  );
}
